import { z } from "zod";
import { ZodDateString } from "../zod-types/zod-date-string";
import { ZodNumberNullable } from "../zod-types/zod-number-nullable";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

const ZodDateNullable = ZodDateString.nullable();
// `ZodNumberNullable` is typed as a plain number; the amounts here are null until they are known.
const ZodAmountNullable = ZodNumberNullable.nullable();

/**
 * An incoming invoice as a person corrects it: the fields the pipeline read, grouped as the
 * extraction groups them, and the workspace and supplier it is booked to. Everything is nullable
 * but the kind and the currency, because an invoice is kept even while a field is unknown — the
 * review checks are what say which ones are still missing.
 *
 * The IBAN and BIC are not among them: they stay as the invoice prints them, for the record, and
 * a payment goes to an account the contact holds — never to one typed into an invoice.
 */
export const incomingInvoiceSchema = z.object({
	kind: z.enum(["invoice", "partial-invoice", "final-invoice", "credit-note"]),
	number: ZodStringNullable,
	invoiceDate: ZodDateNullable,
	dueDate: ZodDateNullable,
	currency: z.string().trim().length(3),
	workspaceId: ZodStringNullable,
	contactId: ZodStringNullable,
	reportNumber: ZodStringNullable,
	netAmount: ZodAmountNullable,
	vatRate: ZodAmountNullable,
	vatAmount: ZodAmountNullable,
	grossAmount: ZodAmountNullable,
	skontoRate: ZodAmountNullable,
	skontoAmount: ZodAmountNullable,
	skontoDeadline: ZodDateNullable,
	issuerName: ZodStringNullable,
	issuerStreet: ZodStringNullable,
	issuerHouseNumber: ZodStringNullable,
	issuerZipCode: ZodStringNullable,
	issuerCity: ZodStringNullable,
	issuerCountry: ZodStringNullable,
	issuerVatId: ZodStringNullable,
	issuerTaxNumber: ZodStringNullable,
	issuerEmail: ZodStringNullable,
	issuerPhone: ZodStringNullable,
});

export type IncomingInvoiceSchema = z.infer<typeof incomingInvoiceSchema>;
